import React, { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ComparatorContainer from './comparator/components/ComparatorContainer';
import QuotationsContainer from './casco/components/QuotationsListContainer';
import { INSURANCE_QUOTATIONS_DATA_QUERY, INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { COMPUTE_QUOTATIONS } from '../general/actions/mutations';
import { useDictionaryHandling, useParams, useQueryWithSimpleErrorHandling, useSimpleError, useInsuranceTypeConfiguration } from 'hooks';
import { useMutation } from 'features/common/security/hooks';
import { insuranceTypeNames, QuotationsStatus } from 'constants/defaultValues';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import { hasHistoryVar, lastModifiedQuotationVar } from './casco/variables';

const InsuranceOfferContainer = ({ documentId, currentStateCode, contextRef }) => {
  const showError = useSimpleError();

  const [insuranceData, setInsuranceData] = useState({});
  const { globalReadOnly } = useContext(InsuranceDataContext);
  const { rcaInsuranceTypeConfiguration, cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.RCA, insuranceTypeNames.CASCO)
  );
  const offerTypeConfigurationJSON = useParams('OfferTypeCodesConfiguration');

  const offerTypeCodes = useMemo(() => {
    if (offerTypeConfigurationJSON) return JSON.parse(offerTypeConfigurationJSON);
  }, [offerTypeConfigurationJSON]);

  useQueryWithSimpleErrorHandling(INSURANCE_QUOTATIONS_DATA_QUERY, {
    variables: { documentId },
    onCompleted: data => {
      setInsuranceData(data?.insuranceData);
    }
  });

  const { getCodeById: getInsuranceTypeCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(
    () => getInsuranceTypeCodeById(insuranceData.insuranceTypeId),
    [getInsuranceTypeCodeById, insuranceData.insuranceTypeId]
  );

  const [computeQuotations] = useMutation(COMPUTE_QUOTATIONS, {
    onError: error => {
      showError(error);
    }
  });

  const handleRecomputeQuotations = useCallback(() => {
    computeQuotations({
      variables: {
        input: { documentId, insuranceTypeCode, recomputeQuotations: true }
      }
    });
  }, [computeQuotations, documentId, insuranceTypeCode]);

  useEffect(
    () => () => {
      lastModifiedQuotationVar(null);
      hasHistoryVar(false);
    },
    []
  );

  return (
    <>
      {insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode && (
        <ComparatorContainer
          documentId={documentId}
          currentStateCode={currentStateCode}
          disableRecompute={
            globalReadOnly({ controlName: 'Comparator.RecomputeQuotations' }) ||
            !contextRef?.current?.properties?.quotationsStatus ||
            contextRef?.current?.properties?.quotationsStatus === QuotationsStatus.PROCESSING ||
            contextRef?.current?.properties?.quotationsStatus === QuotationsStatus.COMPUTING
          }
          recomputeQuotations={handleRecomputeQuotations}
        />
      )}
      {insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode && offerTypeCodes && currentStateCode && (
        <QuotationsContainer
          documentId={documentId}
          insuranceTypeId={insuranceData.insuranceTypeId}
          currentStateCode={currentStateCode}
          contextRef={contextRef}
          disableRecompute={
            globalReadOnly({ controlName: 'Comparator.RecomputeQuotations' }) ||
            !contextRef?.current?.properties?.quotationsStatus ||
            contextRef?.current?.properties?.quotationsStatus === QuotationsStatus.PROCESSING ||
            contextRef?.current?.properties?.quotationsStatus === QuotationsStatus.COMPUTING
          }
          recomputeQuotations={handleRecomputeQuotations}
        />
      )}
    </>
  );
};

InsuranceOfferContainer.propTypes = {
  documentId: PropTypes.number,
  currentStateCode: PropTypes.string,
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default InsuranceOfferContainer;
